/** @jsxImportSource @emotion/react */
import React from 'react'
import {
  teamsContainerStyle,
  teamInfoStyle,
  teamLogoRowStyle,
  teamLogoStyle,
  teamNameStyle,
  vsTextStyle,
} from '../styles'

const PLACEHOLDER_TEAM = '/images/generic_placeholders/placeholder_team.png'

interface TeamDisplay {
  name: string
  logo: string
  /** Optional element shown beside the logo (e.g. the team's game jersey). */
  jersey?: React.ReactNode
}

const renderLogo = (team: TeamDisplay | undefined) => {
  const image = (
    <img
      css={teamLogoStyle}
      src={team?.logo || PLACEHOLDER_TEAM}
      alt={team?.name || 'Placeholder'}
      onError={(e) => {
        e.currentTarget.src = PLACEHOLDER_TEAM
      }}
    />
  )
  return team?.jersey ? (
    <div css={teamLogoRowStyle}>
      {image}
      {team.jersey}
    </div>
  ) : (
    image
  )
}

/**
 * Props for the TeamsContainer component
 */
export interface TeamsContainerProps {
  /** Home team display data */
  homeTeam: TeamDisplay
  /** Away team display data */
  awayTeam: TeamDisplay
  /** Optional extra styles for the container (e.g. drop the card margin). */
  className?: string
}

/**
 * Displays home and away team logos and names with a "VS" separator.
 *
 * @param {TeamsContainerProps} props - Component props
 * @returns {JSX.Element} Team matchup display
 */
const TeamsContainer: React.FC<TeamsContainerProps> = ({
  homeTeam,
  awayTeam,
  className,
}) => {
  return (
    <div css={teamsContainerStyle} className={className}>
      <div css={teamInfoStyle}>
        {renderLogo(homeTeam)}
        <p css={teamNameStyle}>{homeTeam?.name || 'Placeholder'}</p>
      </div>
      <span css={vsTextStyle}>VS.</span>
      <div css={teamInfoStyle}>
        {renderLogo(awayTeam)}
        <p css={teamNameStyle}>{awayTeam?.name || 'Placeholder'}</p>
      </div>
    </div>
  )
}

export default React.memo(TeamsContainer)
